(function(root) {
  "use strict";

  const namespace = root.LegitGrails = root.LegitGrails || {};
  const store = namespace.cookieConsent;
  if (store && store.config && store.config.enabled === false) return;
  let rootNode = null;
  let banner = null;
  let modal = null;
  let dialog = null;
  let lastTrigger = null;
  let draft = null;
  let draftSaleOfData = null;
  let currentState = null;
  const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
  ].join(",");

  const USA_CA_DO_NOT_SELL = store.policy.doNotSellPreset;

  function cloneCategories(categories) {
    categories = categories || {};

    return {
      necessary: true,
      functional: Boolean(categories.functional),
      performance: Boolean(categories.performance),
      analytics: Boolean(categories.analytics),
      advertisement: Boolean(categories.advertisement)
    };
  }

  function categoriesEqual(left, right) {
    left = cloneCategories(left);
    right = cloneCategories(right);

    return Object.keys(left).every(function(category) {
      return left[category] === right[category];
    });
  }

  function getInitialDraft(state) {
    return cloneCategories(state && state.effectiveCategories ? state.effectiveCategories : state && state.categories);
  }

  function hasGpcSignal() {
    return Boolean(root.navigator && root.navigator.globalPrivacyControl === true);
  }

  function getDraftSaleOfData(state) {
    if (!state || state.region !== "USA_CA") return null;
    if (hasGpcSignal()) return false;
    return typeof state.saleOfData === "boolean" ? state.saleOfData : true;
  }

  function setHidden(element, isHidden) {
    if (!element) return;
    element.hidden = Boolean(isHidden);
  }

  function updateDetailsHeight(details) {
    if (!details) return;
    let content = details.querySelector("[data-cc-details-content]");
    let isExpanded = details.getAttribute("aria-hidden") === "false";
    details.style.maxHeight = isExpanded && content ? content.scrollHeight + "px" : "0px";
  }

  function updateOpenDetailsHeights() {
    if (!rootNode) return;
    rootNode.querySelectorAll("[data-cc-details]").forEach(function(details) {
      updateDetailsHeight(details);
    });
  }

  function updateRegionCopies(region) {
    rootNode.querySelectorAll("[data-cc-region-copy]").forEach(function(element) {
      setHidden(element, element.getAttribute("data-cc-region-copy") !== region);
    });
    rootNode.querySelectorAll("[data-cc-region-actions]").forEach(function(element) {
      setHidden(element, element.getAttribute("data-cc-region-actions") !== region);
    });
  }

  function shouldShowBanner(state) {
    return Boolean(
      state &&
      state.isRegionResolved &&
      !state.hasExplicitConsent &&
      !(state.region === "USA_CA" && hasGpcSignal()) &&
      (state.region === "EU" || state.region === "USA_CA")
    );
  }

  /**
   * Renders banner/modal visibility from the current consent snapshot.
   *
   * @param {object|null} state
   * @returns {void}
   */
  function render(state) {
    currentState = state || (store && store.getState ? store.getState() : null);
    if (!currentState || !rootNode) return;

    rootNode.setAttribute("data-cc-region", currentState.region || "EU");
    rootNode.setAttribute("data-cc-ready", currentState.isRegionResolved ? "true" : "false");
    document.querySelectorAll("[data-cc-open-preferences]").forEach(function(trigger) {
      setHidden(trigger, !currentState.isRegionResolved || currentState.region === "ROW");
    });
    updateRegionCopies(currentState.region);
    setHidden(banner, !shouldShowBanner(currentState));
    rootNode.querySelectorAll("[data-cc-primary-copy]").forEach(function(element) {
      let copy = element.getAttribute("data-cc-primary-copy");
      setHidden(element, currentState.region === "USA_CA" ? copy !== "understand" : copy !== "accept");
    });

    if (currentState.region === "ROW" && modal && !modal.hidden) {
      closePreferences();
    }

    if (modal && !modal.hidden) {
      syncDraftControls();
    }
  }

  /**
   * Replaces the modal-only preference draft without persisting consent.
   *
   * @param {object} nextDraft Draft category values.
   * @param {boolean} [saleOfData] USA_CA data-sale choice.
   */
  function setDraft(nextDraft, saleOfData) {
    draft = cloneCategories(nextDraft);
    draftSaleOfData = typeof saleOfData === "boolean"
      ? saleOfData
      : getDraftSaleOfData(currentState);
    syncDraftControls();
  }

  /**
   * Synchronizes modal controls and action availability from the current draft.
   */
  function syncDraftControls() {
    if (!rootNode || !draft) return;
    if (currentState && currentState.region === "USA_CA" && hasGpcSignal()) draftSaleOfData = false;
    const trackingOptedOut = currentState && currentState.region === "USA_CA" && hasGpcSignal();
    if (trackingOptedOut) {
      draft.analytics = false;
      draft.advertisement = false;
    }

    rootNode.querySelectorAll("[data-cc-category-input]").forEach(function(input) {
      let category = input.getAttribute("data-cc-category-input");
      input.checked = Boolean(draft[category]);
      input.disabled = Boolean(trackingOptedOut && (category === "analytics" || category === "advertisement"));
    });

    let doNotSell = rootNode.querySelector("[data-cc-do-not-sell]");
    let doNotSellBlock = rootNode.querySelector("[data-cc-do-not-sell-block]");

    if (doNotSellBlock) {
      setHidden(doNotSellBlock, !currentState || currentState.region !== "USA_CA");
    }
    if (doNotSell) {
      // Exact category preset, not a synonym for the native saleOfData opt-out.
      doNotSell.checked = currentState && currentState.region === "USA_CA" && categoriesEqual(draft, USA_CA_DO_NOT_SELL);
      doNotSell.disabled = Boolean(currentState && currentState.region === "USA_CA" && hasGpcSignal());
    }

    let isDecisionPending = !currentState || !currentState.hasExplicitConsent;
    let currentCategories = getInitialDraft(currentState);
    let currentSaleOfData = getDraftSaleOfData(currentState);
    let primaryCategories = currentState && currentState.region === "USA_CA" ? USA_CA_DO_NOT_SELL : {
      necessary: true,
      functional: true,
      performance: true,
      analytics: true,
      advertisement: true
    };
    let saveButton = rootNode.querySelector("[data-cc-action='save-preferences']");
    let primaryButton = rootNode.querySelector("[data-cc-action='modal-primary']");

    if (saveButton) {
      saveButton.disabled = !isDecisionPending &&
        categoriesEqual(draft, currentCategories) &&
        draftSaleOfData === currentSaleOfData;
    }
    if (primaryButton) {
      primaryButton.disabled = !isDecisionPending && categoriesEqual(draft, primaryCategories) &&
        (currentState.region !== "USA_CA" || draftSaleOfData === false);
    }
  }

  function resetDetails() {
    if (!rootNode) return;
    rootNode.querySelectorAll("[data-cc-details-toggle]").forEach(function(toggle) {
      toggle.setAttribute("aria-expanded", "false");
    });
    rootNode.querySelectorAll("[data-cc-details]").forEach(function(details) {
      details.setAttribute("aria-hidden", "true");
      updateDetailsHeight(details);
    });
  }

  function getFocusableElements() {
    if (!dialog) return [];
    return Array.prototype.slice.call(dialog.querySelectorAll(focusableSelector)).filter(function(element) {
      return !element.hasAttribute("disabled") && element.offsetParent !== null;
    });
  }

  /**
   * Opens the preferences modal with an isolated draft.
   *
   * Changes are not persisted until the user saves preferences or chooses a
   * primary action.
   *
   * @param {HTMLElement|null} trigger
   * @returns {void}
   */
  function openPreferences(trigger) {
    if (!modal || !dialog) return;

    let state = currentState || store.getState();
    if (!state || !state.isRegionResolved || state.region === "ROW") return;

    lastTrigger = trigger && trigger.focus ? trigger : document.activeElement;
    setDraft(getInitialDraft(state), getDraftSaleOfData(state));
    resetDetails();
    modal.hidden = false;
    modal.setAttribute("aria-hidden", "false");
    document.body.classList.add("cc-modal-open");

    root.setTimeout(function() {
      let focusable = getFocusableElements();
      (focusable[0] || dialog).focus();
    }, 0);
  }

  /**
   * Closes the preferences modal and returns focus to its originating trigger.
   */
  function closePreferences() {
    if (!modal) return;

    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("cc-modal-open");
    resetDetails();

    if (lastTrigger && typeof lastTrigger.focus === "function") {
      lastTrigger.focus();
    }
  }

  /**
   * Persists the modal draft into the shared consent store.
   *
   * @returns {void}
   */
  function savePreferences() {
    if (!store || typeof store.setConsent !== "function" || !draft) return;
    store.setConsent(draft, { saleOfData: draftSaleOfData });
    closePreferences();
  }

  function acceptAll() {
    if (store && typeof store.acceptAll === "function") {
      store.acceptAll({
        saleOfData: currentState && currentState.region === "USA_CA" ? true : null
      });
    }
    closePreferences();
  }

  function applyPrimaryPreset() {
    if (currentState && currentState.region === "USA_CA") {
      store.applyDoNotSellPreset();
      closePreferences();
    } else acceptAll();
  }

  /**
   * Routes a consent UI action to the appropriate store or modal operation.
   *
   * @param {string} action Action identifier from data-cc-action.
   * @param {HTMLElement} trigger Triggering control.
   */
  function handleAction(action, trigger) {
    if (action === "accept-all") {
      acceptAll();
      return;
    }

    if (action === "understand") {
      applyPrimaryPreset();
      return;
    }

    if (action === "save-preferences") {
      savePreferences();
      return;
    }

    if (action === "modal-primary") {
      applyPrimaryPreset();
      return;
    }

    if (action === "open-preferences") {
      openPreferences(trigger);
    }
  }

  function onDocumentClick(event) {
    let openTrigger = event.target.closest("[data-cc-open-preferences]");
    if (openTrigger) {
      event.preventDefault();
      openPreferences(openTrigger);
      return;
    }

    let actionTrigger = event.target.closest("[data-cc-action]");
    if (actionTrigger && rootNode && rootNode.contains(actionTrigger)) {
      event.preventDefault();
      handleAction(actionTrigger.getAttribute("data-cc-action"), actionTrigger);
      return;
    }

    if (event.target.closest("[data-cc-close]")) {
      event.preventDefault();
      closePreferences();
    }
  }

  function onInputChange(event) {
    let categoryInput = event.target.closest("[data-cc-category-input]");
    if (categoryInput) {
      let category = categoryInput.getAttribute("data-cc-category-input");
      draft = cloneCategories(draft);
      draft[category] = Boolean(categoryInput.checked);
      if ((category === "analytics" || category === "advertisement") && categoryInput.checked && !hasGpcSignal()) draftSaleOfData = true;
      syncDraftControls();
      return;
    }

    if (event.target.matches("[data-cc-do-not-sell]")) {
      if (event.target.checked) {
        setDraft(USA_CA_DO_NOT_SELL, false);
      } else {
        setDraft(Object.assign({}, draft, { analytics: true, advertisement: true }), true);
      }
    }
  }

  function onDetailsToggle(event) {
    let toggle = event.target.closest("[data-cc-details-toggle]");
    if (!toggle || !rootNode.contains(toggle)) return;

    let category = toggle.getAttribute("data-cc-details-toggle");
    let details = rootNode.querySelector("[data-cc-details='" + category + "']");
    let isExpanded = toggle.getAttribute("aria-expanded") === "true";

    toggle.setAttribute("aria-expanded", String(!isExpanded));
    details.setAttribute("aria-hidden", String(isExpanded));
    updateDetailsHeight(details);
  }

  function onKeydown(event) {
    if (!modal || modal.hidden) return;

    if (event.key === "Escape") {
      event.preventDefault();
      closePreferences();
      return;
    }

    if (event.key !== "Tab") return;

    let focusable = getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    let first = focusable[0];
    let last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function bind() {
    document.addEventListener("click", onDocumentClick);
    document.addEventListener("change", onInputChange);
    document.addEventListener("click", onDetailsToggle);
    document.addEventListener("keydown", onKeydown);
    root.addEventListener("resize", updateOpenDetailsHeights);
    document.addEventListener("legitgrails:cookie-consent-open-preferences", function(event) {
      openPreferences(event.detail && event.detail.trigger);
    });

    if (store && typeof store.subscribe === "function") {
      store.subscribe(function(state) {
        render(state);
      });
    }

    document.addEventListener("legitgrails:cookie-consent-ready", function(event) {
      render(event.detail && event.detail.state);
    });
  }

  function init() {
    rootNode = document.querySelector("[data-cc-root]");
    if (!rootNode || !store || typeof store.getState !== "function") return;

    banner = rootNode.querySelector("[data-cc-banner]");
    modal = rootNode.querySelector("[data-cc-modal]");
    dialog = rootNode.querySelector("[data-cc-dialog]");
    bind();
    updateOpenDetailsHeights();
    render(store.getState());
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})(window);
